import { runtimeConfig } from "./runtimeConfig";

// Tokens live in sessionStorage: tab-scoped, gone when the tab closes, and the
// hosted UI's own cookie makes a new tab's login a redirect rather than a prompt.
const STORAGE_KEY = "ardley-crm.cognito";
// Where the pre-redirect PKCE verifier and state wait for the callback.
const PENDING_KEY = "ardley-crm.cognito.pending";

// Refresh this long before expiry so a request never leaves with a dying token.
const REFRESH_MARGIN_MS = 60_000;

export interface CognitoSession {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
  email?: string;
  name?: string;
  sub?: string;
  customerId?: string;
}

interface TokenResponse {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
}

export function cognitoConfig() {
  const {
    cognitoUserPoolId: poolId,
    cognitoClientId: clientId,
    cognitoDomain: domain,
  } = runtimeConfig();
  const redirectUri =
    import.meta.env.VITE_COGNITO_REDIRECT_URI ||
    `${window.location.origin}/auth/callback`;
  const logoutUri =
    import.meta.env.VITE_COGNITO_LOGOUT_URI || `${window.location.origin}/`;
  return { poolId, clientId, domain, redirectUri, logoutUri };
}

export function isCognitoConfigured(): boolean {
  const { poolId, clientId, domain } = cognitoConfig();
  return Boolean(poolId && clientId && domain);
}

function requireConfig() {
  const config = cognitoConfig();
  if (!config.clientId || !config.domain) {
    throw new Error("cognito_not_configured");
  }
  return config as typeof config & { clientId: string; domain: string };
}

/** The stored session, expired or not. Use `getValidSession` before a request. */
export function readSession(): CognitoSession | null {
  if (typeof window === "undefined") return null;
  // Sessions used to be kept in localStorage; drop any leftover copy.
  window.localStorage.removeItem(STORAGE_KEY);
  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CognitoSession;
  } catch {
    return null;
  }
}

export function writeSession(session: CognitoSession) {
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearSession() {
  window.sessionStorage.removeItem(STORAGE_KEY);
  window.localStorage.removeItem(STORAGE_KEY);
}

function isFresh(session: CognitoSession): boolean {
  return session.expiresAt - REFRESH_MARGIN_MS > Date.now();
}

function decodeJwt(token: string): Record<string, unknown> {
  const payload = token.split(".")[1];
  const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
  return JSON.parse(json) as Record<string, unknown>;
}

export function sessionFromTokens(tokens: TokenResponse): CognitoSession {
  const claims = decodeJwt(tokens.id_token);
  const expiresIn = tokens.expires_in ?? 3600;
  return {
    idToken: tokens.id_token,
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    expiresAt: Date.now() + expiresIn * 1000,
    email: typeof claims.email === "string" ? claims.email : undefined,
    name:
      typeof claims.name === "string"
        ? claims.name
        : [claims.given_name, claims.family_name].filter(Boolean).join(" ") ||
          undefined,
    sub: typeof claims.sub === "string" ? claims.sub : undefined,
    customerId:
      typeof claims["custom:customerId"] === "string"
        ? claims["custom:customerId"]
        : undefined,
  };
}

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function randomToken(): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(32)));
}

async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return base64Url(new Uint8Array(digest));
}

export interface AuthCallback {
  code: string;
  state: string | null;
}

function callbackParams(): URLSearchParams | null {
  const fromSearch = new URLSearchParams(window.location.search);
  if (fromSearch.get("code")) return fromSearch;
  // HashRouter puts the real path after #. Cognito still returns ?code= on
  // the pre-hash URL; if a later rewrite stuffed it after the hash, read that.
  const hash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  const qIndex = hash.indexOf("?");
  if (qIndex === -1) return null;
  const fromHash = new URLSearchParams(hash.slice(qIndex + 1));
  return fromHash.get("code") ? fromHash : null;
}

/**
 * Reads `?code=&state=` and strips them from the URL straight away. The code is
 * single-use, so it must not survive to a second `checkAuth` or a reload.
 */
export function consumeAuthCallback(): AuthCallback | null {
  const params = callbackParams();
  if (!params) return null;
  window.history.replaceState({}, document.title, "/");
  return { code: params.get("code")!, state: params.get("state") };
}

/** Hosted-UI authorize URL with PKCE (S256) and a `state` the callback must echo. */
export async function hostedLoginUrl(): Promise<string> {
  const { clientId, domain, redirectUri } = requireConfig();
  const verifier = randomToken();
  const state = randomToken();
  window.sessionStorage.setItem(
    PENDING_KEY,
    JSON.stringify({ verifier, state }),
  );
  const url = new URL(`${domain}/oauth2/authorize`);
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", "openid email profile");
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("state", state);
  url.searchParams.set("code_challenge", await pkceChallenge(verifier));
  url.searchParams.set("code_challenge_method", "S256");
  return url.toString();
}

export function hostedLogoutUrl(): string {
  const { clientId, domain, logoutUri } = requireConfig();
  const url = new URL(`${domain}/logout`);
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("logout_uri", logoutUri);
  return url.toString();
}

async function postToken(body: Record<string, string>): Promise<TokenResponse> {
  const { domain } = requireConfig();
  const res = await fetch(`${domain}/oauth2/token`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams(body),
  });
  if (!res.ok) {
    throw new Error(`cognito_token ${res.status}`);
  }
  return (await res.json()) as TokenResponse;
}

// The exchange in flight, if any. StrictMode and ra-core both call checkAuth
// on load; the first one strips the code from the URL, so later calls must
// wait on this rather than conclude there is no session.
let activeLogin: Promise<CognitoSession> | null = null;

export function loginInProgress(): Promise<CognitoSession> | null {
  return activeLogin;
}

/**
 * Exchanges a callback for tokens and stores the session. Refuses a callback
 * whose `state` does not match the one this tab sent (login CSRF / code
 * injection), or one this tab never started.
 */
export function completeLogin(callback: AuthCallback): Promise<CognitoSession> {
  activeLogin = (async () => {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    window.sessionStorage.removeItem(PENDING_KEY);
    const pending = raw
      ? (JSON.parse(raw) as { verifier: string; state: string })
      : null;
    if (!pending || !callback.state || pending.state !== callback.state) {
      throw new Error("cognito_state_mismatch");
    }
    const { clientId, redirectUri } = requireConfig();
    const tokens = await postToken({
      grant_type: "authorization_code",
      client_id: clientId,
      code: callback.code,
      redirect_uri: redirectUri,
      code_verifier: pending.verifier,
    });
    const session = sessionFromTokens(tokens);
    writeSession(session);
    return session;
  })().finally(() => {
    activeLogin = null;
  });
  return activeLogin;
}

let refreshing: Promise<CognitoSession | null> | null = null;

async function refreshSession(
  session: CognitoSession,
): Promise<CognitoSession | null> {
  if (!session.refreshToken) return null;
  try {
    const { clientId } = requireConfig();
    const tokens = await postToken({
      grant_type: "refresh_token",
      client_id: clientId,
      refresh_token: session.refreshToken,
    });
    // Cognito does not rotate the refresh token; keep the one we have.
    const next = sessionFromTokens({
      ...tokens,
      refresh_token: tokens.refresh_token ?? session.refreshToken,
    });
    writeSession(next);
    return next;
  } catch {
    // Revoked or expired refresh token: the caller treats this as logged out.
    clearSession();
    return null;
  }
}

/** A session good for at least another minute, refreshing if needed; else null. */
export async function getValidSession(): Promise<CognitoSession | null> {
  const session = readSession();
  if (!session) return null;
  if (isFresh(session)) return session;
  refreshing ??= refreshSession(session).finally(() => {
    refreshing = null;
  });
  return refreshing;
}

/** Best effort: invalidate the refresh token server-side on logout. */
export function revokeRefreshToken(session: CognitoSession | null) {
  if (!session?.refreshToken) return;
  const { clientId, domain } = requireConfig();
  void fetch(`${domain}/oauth2/revoke`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      token: session.refreshToken,
      client_id: clientId,
    }),
    // The page navigates to the hosted logout right after this.
    keepalive: true,
  }).catch(() => undefined);
}
